import { AlertTriangle, Building, CheckCircle, Database, Trash2, UserCog, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useUser } from '../../contextapi/UserContext';
import { useToast } from '../../hooks/use-toast';
import { supabase } from '../../utils/supabaseClient';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface AccountDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeletionComplete?: () => void;
}

interface DeletionDependencies {
  organizations: {
    count: number;
    names: string[];
  };
  organizations_user_is_only_member: number;
  groups: {
    count: number;
    names: string[];
  };
  groups_user_is_only_member: number;
  user_data_records: number;
}

interface OrganizationMember {
  userId: string;
  name: string;
  email: string;
  currentRole: string;
  organisation_id: string;
}

interface GroupMember {
  userId: string;
  name: string;
  email: string;
  currentRole: string;
  group_id: string;
}

interface AdminTransferData {
  organizations: {
    [orgId: string]: {
      name: string;
      members: OrganizationMember[];
      selectedAdmins: string[];
      transferred: boolean;
    };
  };
  groups: {
    [groupId: string]: {
      name: string;
      organizationName: string;
      members: GroupMember[];
      selectedAdmins: string[];
      transferred: boolean;
    };
  };
}

export const AccountDeletionModal: React.FC<AccountDeletionModalProps> = ({
  isOpen,
  onClose,
  onDeletionComplete
}) => {
  const { user, session } = useUser();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'admin_transfer' | 'dependencies' | 'confirmation' | 'processing' | null>(null);
  const [dependencies, setDependencies] = useState<DeletionDependencies | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [reason, setReason] = useState('');
  const [deletionType, setDeletionType] = useState<'profile' | 'full'>('profile');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationDetails, setValidationDetails] = useState<{ orgs?: string[], groups?: string[] } | null>(null);
  const [adminTransferData, setAdminTransferData] = useState<AdminTransferData>({
    organizations: {},
    groups: {}
  });

  useEffect(() => {
    if (isOpen && user?.id) {
      loadDependencies();
    }
  }, [isOpen, user?.id]);

  // Automatically determine deletion type and navigate to appropriate step based on dependencies
  useEffect(() => {
    if (dependencies && user?.id) {
      const isOnlyMemberInAllOrgs = dependencies.organizations_user_is_only_member === dependencies.organizations.count;
      const hasGroups = dependencies.groups && dependencies.groups.count > 0;
      const isOnlyMemberInAllGroups = hasGroups
        ? dependencies.groups_user_is_only_member === dependencies.groups.count
        : true; // No groups means condition is met

      // Full deletion if user is only member in ALL orgs AND ALL groups
      if (isOnlyMemberInAllOrgs && isOnlyMemberInAllGroups) {
        setDeletionType('full');
        setValidationError(null);
        // For full deletion, go directly to dependencies step
        setStep('dependencies');
      } else {
        // Profile-only deletion if there are other members in any org or group
        setDeletionType('profile');
        setValidationError(null);
        // For profile deletion, validate first then navigate
        validateAndNavigateForProfile();
      }
    }
  }, [dependencies, user?.id]);

  const validateAndNavigateForProfile = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const validation = await validateProfileDeletion();

      if (!validation.can_proceed) {
        // Set validation error with details
        setValidationError(validation.message);

        if (validation.organizations_requiring_admin || validation.groups_requiring_admin) {
          setValidationDetails({
            orgs: validation.organizations_requiring_admin || [],
            groups: validation.groups_requiring_admin || []
          });

          // Load members for admin transfer
          await loadMembersForAdminTransfer(
            validation.organizations_requiring_admin || [],
            validation.groups_requiring_admin || []
          );

          // Go to admin transfer step
          setStep('admin_transfer');
        } else {
          toast({
            title: 'Cannot Delete Account',
            description: validation.message,
            variant: 'destructive',
          });
        }
      } else {
        // Validation passed, proceed to dependencies step
        setValidationError(null);
        setValidationDetails(null);
        setStep('dependencies');
      }
    } catch (error) {
      console.error('Error during validation:', error);
      toast({
        title: 'Validation Error',
        description: 'Failed to validate deletion prerequisites. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDependencies = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase.rpc('check_deletion_dependencies', {
        p_user_id: user.id
      });

      if (error) {
        throw error;
      }

      setDependencies(data);
    } catch (error) {
      console.error('Error loading dependencies:', error);
      toast({
        title: 'Error',
        description: 'Failed to load account dependencies. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const loadMembersForAdminTransfer = async (orgNames: string[], groupNames: string[]) => {
    if (!user?.id) return;

    try {
      console.log('orgNames', orgNames);
      console.log('groupNames', groupNames);

      const newTransferData: AdminTransferData = {
        organizations: {},
        groups: {}
      };

      console.log('orgNames', orgNames);

      // Load members for each organization
      for (const orgName of orgNames) {
        // Query through base_organisation_members to get the tenant_id for the organization
        // where the current user is a member. This avoids duplicate tenant_name issues.
        // organisation_id in base_organisation_members IS the tenant_id we need.
        const { data: orgMembership, error: orgError } = await supabase
          .from('base_organisation_members')
          .select('organisation_id, base_tenants!inner(tenant_name)')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .eq('base_tenants.tenant_name', orgName)
          .maybeSingle();

        console.log('orgMembership', orgMembership);
        console.log('orgError', orgError);

        if (orgMembership && orgMembership.organisation_id) {
          const tenantId = orgMembership.organisation_id;

          const { data: members, error } = await supabase.rpc('get_organization_members', {
            org_id: tenantId
          });
          console.log('tenantId', tenantId);
          console.log('members', members);

          if (!error && members) {
            console.log('Raw organization members from RPC:', members);
            const orgMembers = members
              .filter((m: any) => m.user_id !== user.id) // Exclude current user
              .map((m: any) => ({
                userId: m.user_id,
                name: '', // Will be fetched from profiles
                email: '', // Will be fetched from profiles
                currentRole: m.role,
                organisation_id: m.organisation_id
              }));
            console.log('Filtered orgMembers:', orgMembers);

            // Fetch user details for all members at once
            const userIds = orgMembers.map(m => m.userId);
            console.log('Fetching profiles for userIds:', userIds);

            if (userIds.length > 0) {
              let profiles = [];

              // Use a more reliable method to fetch profiles
              if (userIds.length === 1) {
                const { data: profileData, error: profileError } = await supabase
                  .from('base_profiles')
                  .select('id, name, email')
                  .eq('id', userIds[0])
                  .maybeSingle();

                if (profileError) {
                  console.log('Profile query error:', profileError);
                } else if (profileData) {
                  profiles = [profileData];
                }
              } else {
                const { data: profileData, error: profileError } = await supabase
                  .from('base_profiles')
                  .select('id, name, email')
                  .in('id', userIds);

                if (profileError) {
                  console.log('Profile query error:', profileError);
                } else {
                  profiles = profileData || [];
                }
              }

              console.log('Profile query result:', profiles);

              // Map profile data to members
              if (profiles && profiles.length > 0) {
                const profileMap = new Map(profiles.map(p => [p.id, p]));
                orgMembers.forEach((member, index) => {
                  const profile = profileMap.get(member.userId);
                  if (profile) {
                    orgMembers[index].name = profile.name || 'Unknown User';
                    orgMembers[index].email = profile.email || '';
                  }
                });
              }
            }

            newTransferData.organizations[tenantId] = {
              name: orgName,
              members: orgMembers,
              selectedAdmins: [],
              transferred: false
            };
          }
        }
      }

      console.log('newTransferData', newTransferData);

      // Load members for each group
      for (const groupName of groupNames) {
        const { data: groupData } = await supabase
          .from('base_groups')
          .select('id, tenant_id')
          .eq('name', groupName)
          .maybeSingle();

        console.log('groupData', groupData);

        if (groupData) {
          // Get organization name
          const { data: orgData } = await supabase
            .from('base_tenants')
            .select('tenant_name')
            .eq('tenant_id', groupData.tenant_id)
            .maybeSingle();

          const { data: members, error } = await supabase.rpc('get_team_members', {
            team_id: groupData.id
          });

          if (!error && members) {
            console.log('Raw group members from RPC:', members);
            const groupMembers = members
              .filter((m: any) => m.user_id !== user.id) // Exclude current user
              .map((m: any) => ({
                userId: m.user_id,
                name: '', // Will be fetched from profiles
                email: '', // Will be fetched from profiles
                currentRole: m.role,
                group_id: m.group_id
              }));
            console.log('Filtered groupMembers:', groupMembers);

            // Fetch user details for all members at once
            const userIds = groupMembers.map(m => m.userId);
            console.log('Fetching group profiles for userIds:', userIds);

            if (userIds.length > 0) {
              let profiles = [];

              // Use a more reliable method to fetch profiles
              if (userIds.length === 1) {
                const { data: profileData, error: profileError } = await supabase
                  .from('base_profiles')
                  .select('id, name, email')
                  .eq('id', userIds[0])
                  .maybeSingle();

                if (profileError) {
                  console.log('Group profile query error:', profileError);
                } else if (profileData) {
                  profiles = [profileData];
                }
              } else {
                const { data: profileData, error: profileError } = await supabase
                  .from('base_profiles')
                  .select('id, name, email')
                  .in('id', userIds);

                if (profileError) {
                  console.log('Group profile query error:', profileError);
                } else {
                  profiles = profileData || [];
                }
              }

              console.log('Group profile query result:', profiles);

              // Map profile data to members
              if (profiles && profiles.length > 0) {
                const profileMap = new Map(profiles.map(p => [p.id, p]));
                groupMembers.forEach((member, index) => {
                  const profile = profileMap.get(member.userId);
                  if (profile) {
                    groupMembers[index].name = profile.name || 'Unknown User';
                    groupMembers[index].email = profile.email || '';
                  }
                });
              }
            }

            console.log('groupMembers', groupMembers);

            newTransferData.groups[groupData.id] = {
              name: groupName,
              organizationName: orgData?.tenant_name || 'Unknown',
              members: groupMembers,
              selectedAdmins: [],
              transferred: false
            };
          }
        }
      }

      setAdminTransferData(newTransferData);
      console.log('newTransferData', newTransferData);
    } catch (error) {
      console.error('Error loading members for admin transfer:', error);
      toast({
        title: 'Error',
        description: 'Failed to load members for admin transfer.',
        variant: 'destructive',
      });
    }
  };

  const handleOrgAdminSelection = (orgId: string, userId: string, selected: boolean) => {
    setAdminTransferData(prev => {
      const updated = { ...prev };
      if (updated.organizations[orgId]) {
        if (selected) {
          updated.organizations[orgId].selectedAdmins.push(userId);
        } else {
          updated.organizations[orgId].selectedAdmins =
            updated.organizations[orgId].selectedAdmins.filter(id => id !== userId);
        }
      }
      return updated;
    });
  };

  const handleGroupAdminSelection = (groupId: string, userId: string, selected: boolean) => {
    setAdminTransferData(prev => {
      const updated = { ...prev };
      if (updated.groups[groupId]) {
        if (selected) {
          updated.groups[groupId].selectedAdmins.push(userId);
        } else {
          updated.groups[groupId].selectedAdmins =
            updated.groups[groupId].selectedAdmins.filter(id => id !== userId);
        }
      }
      return updated;
    });
  };

  const handleConfirmOrgAdminTransfer = async (orgId: string) => {
    const orgData = adminTransferData.organizations[orgId];
    if (!orgData || orgData.selectedAdmins.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select at least one admin for this organization.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      for (const userId of orgData.selectedAdmins) {
        const { error } = await supabase
          .from('base_organisation_members')
          .update({ role: 'org_admin' })
          .eq('organisation_id', orgId)
          .eq('user_id', userId);

        if (error) throw error;
      }

      // Mark as transferred
      setAdminTransferData(prev => ({
        ...prev,
        organizations: {
          ...prev.organizations,
          [orgId]: { ...prev.organizations[orgId], transferred: true }
        }
      }));

      toast({
        title: 'Success',
        description: `Admin rights transferred for ${orgData.name}.`,
      });
    } catch (error) {
      console.error('Error transferring admin rights:', error);
      toast({
        title: 'Error',
        description: 'Failed to transfer admin rights. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmGroupAdminTransfer = async (groupId: string) => {
    const groupData = adminTransferData.groups[groupId];
    if (!groupData || groupData.selectedAdmins.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select at least one admin for this group.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      for (const userId of groupData.selectedAdmins) {
        const { error } = await supabase
          .from('base_group_members')
          .update({ role: 'admin' })
          .eq('group_id', groupId)
          .eq('user_id', userId);

        if (error) throw error;
      }

      // Mark as transferred
      setAdminTransferData(prev => ({
        ...prev,
        groups: {
          ...prev.groups,
          [groupId]: { ...prev.groups[groupId], transferred: true }
        }
      }));

      toast({
        title: 'Success',
        description: `Admin rights transferred for ${groupData.name}.`,
      });
    } catch (error) {
      console.error('Error transferring admin rights:', error);
      toast({
        title: 'Error',
        description: 'Failed to transfer admin rights. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const canProceedAfterTransfer = () => {
    // Check if all organizations have been transferred (not just selected)
    const allOrgsTransferred = Object.values(adminTransferData.organizations)
      .every(org => org.transferred);

    // Check if all groups have been transferred (not just selected)
    const allGroupsTransferred = Object.values(adminTransferData.groups)
      .every(group => group.transferred);

    return allOrgsTransferred && allGroupsTransferred;
  };

  const validateProfileDeletion = async () => {
    if (!user?.id || deletionType !== 'profile') return { can_proceed: true };

    try {
      const { data, error } = await supabase.rpc('validate_profile_deletion_prerequisites', {
        p_user_id: user.id
      });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error validating profile deletion:', error);
      return { can_proceed: false, message: 'Failed to validate deletion prerequisites' };
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;

    setLoading(true);
    setStep('processing');

    try {
      // 1. Check deletion impact first to identify Teable spaces
      console.log("[AccountDeletion] Checking deletion impact...");
      const { data: impactData, error: impactError } = await supabase
        .rpc('get_user_deletion_impact', {
          p_user_id: user.id
        });

      if (impactError) {
        console.error("[AccountDeletion] ❌ Impact check failed:", impactError);
        // Fallback: proceed with normal deletion if impact check fails
      } else {
        console.log("[AccountDeletion] Impact analysis:", impactData);

        // 2. Delete Teable spaces FIRST (while DB credentials still exist)
        if (impactData.teable_spaces_to_delete && impactData.teable_spaces_to_delete.length > 0) {
          console.log(`[AccountDeletion] Found ${impactData.teable_spaces_to_delete.length} Teable spaces to delete first`);

          for (const spaceInfo of impactData.teable_spaces_to_delete) {
            try {
              console.log(`[AccountDeletion] Deleting Teable space: ${spaceInfo.spaceId}`);
              const response = await fetch(`/api/teable/spaces/${spaceInfo.spaceId}`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${session?.access_token}`,
                  'Content-Type': 'application/json'
                }
              });

              const result = await response.json();
              console.log(`[AccountDeletion] Teable deletion result for ${spaceInfo.spaceId}:`, result);

              if (!response.ok) {
                console.error(`[AccountDeletion] ❌ Failed to delete Teable space ${spaceInfo.spaceId}:`, result);
              }
            } catch (err) {
              console.error(`[AccountDeletion] ❌ Error calling Teable deletion API:`, err);
            }
          }
        }
      }

      // 3. Proceed with Database Deletion
      let dbDeletionResult;
      let dbError;

      if (deletionType === 'profile') {
        console.log("[AccountDeletion] executing delete_user_profile_only...");
        const result = await supabase.rpc('delete_user_profile_only', {
          p_user_id: user.id,
          p_reason: reason || null
        });
        dbDeletionResult = result.data;
        dbError = result.error;
      } else {
        console.log("[AccountDeletion] executing comprehensive_user_deletion...");
        const result = await supabase.rpc('comprehensive_user_deletion', {
          p_user_id: user.id,
          p_deletion_type: deletionType,
          p_reason: reason || null
        });
        dbDeletionResult = result.data;
        dbError = result.error;
      }

      if (dbError) throw dbError;

      console.log("[AccountDeletion] DB deletion successful:", dbDeletionResult);

      if (dbDeletionResult.success) {
        // 4. Finally, delete Auth User (prevents login)
        const shouldDeleteAuth = dbDeletionResult.delete_auth_user || impactData?.requires_auth_deletion;

        if (shouldDeleteAuth) {
          console.log("[AccountDeletion] Deleting Auth user via backend...");
          const authResponse = await fetch('/api/auth/delete-user', {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`,
              'Content-Type': 'application/json'
            }
          });

          const authResult = await authResponse.json();
          console.log("[AccountDeletion] Auth deletion response:", authResponse.status, authResult);

          if (!authResponse.ok) {
            console.error("[AccountDeletion] ❌ Failed to delete auth user:", authResult);
          } else {
            console.log("[AccountDeletion] ✅ Auth user deleted successfully");
          }
        }

        toast({
          title: 'Account Deleted',
          description: 'Your account has been successfully deleted.',
        });

        onDeletionComplete?.();
        onClose();

        // Sign out the user
        await supabase.auth.signOut();
        window.location.href = '/login';
      } else {
        // Handle validation errors
        if (dbDeletionResult.validation_error) {
          const validation = dbDeletionResult.validation_error;
          const orgs = validation.organizations_requiring_admin || [];
          const groups = validation.groups_requiring_admin || [];

          setValidationError(validation.message);
          setValidationDetails({ orgs, groups });

          toast({
            title: 'Cannot Delete Account',
            description: validation.message,
            variant: 'destructive',
          });
        } else {
          throw new Error(dbDeletionResult.error || 'Deletion failed');
        }
      }
    } catch (error) {
      console.error('[AccountDeletion] Error deleting account:', error);
      toast({
        title: 'Deletion Failed',
        description: error instanceof Error ? error.message : 'Failed to delete account. Please try again.',
        variant: 'destructive',
      });
      setStep('confirmation');
    } finally {
      setLoading(false);
    }
  };

  const renderAdminTransferStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <UserCog className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Transfer Admin Rights</h3>
        <p className="text-gray-600">
          Before deleting your account, please assign new administrators
        </p>
      </div>

      {/* Organizations Section */}
      {Object.keys(adminTransferData.organizations).length > 0 && (
        <div className="space-y-4">
          <div className="border-b pb-2">
            <h4 className="font-semibold text-lg flex items-center gap-2">
              <Building className="w-5 h-5" />
              Organizations
            </h4>
            <p className="text-sm text-gray-600 mt-1">
              Select new admins for these organizations
            </p>
          </div>

          {Object.entries(adminTransferData.organizations).map(([orgId, orgData]) => (
            <div key={orgId} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium">{orgData.name}</h5>
                {orgData.transferred && (
                  <span className="text-green-600 text-sm flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Transferred
                  </span>
                )}
              </div>

              {orgData.members.length === 0 ? (
                <div className="text-sm text-gray-500 italic p-4 border border-dashed rounded">
                  No other members in this organization
                </div>
              ) : (
                <>
                  <div className="space-y-2 mb-4">
                    {orgData.members.map((member) => (
                      <label
                        key={member.userId}
                        className="flex items-center gap-3 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={orgData.selectedAdmins.includes(member.userId)}
                          onChange={(e) => handleOrgAdminSelection(orgId, member.userId, e.target.checked)}
                          disabled={orgData.transferred}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{member.name}</div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                          <div className="text-xs text-gray-400">Current: {member.currentRole}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <Button
                    onClick={() => handleConfirmOrgAdminTransfer(orgId)}
                    disabled={orgData.transferred || orgData.selectedAdmins.length === 0 || loading}
                    className="w-full"
                  >
                    {orgData.transferred ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Admin Transferred
                      </>
                    ) : (
                      <>Assign {orgData.selectedAdmins.length || 0} Selected as Admin</>
                    )}
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Groups Section */}
      {Object.keys(adminTransferData.groups).length > 0 && (
        <div className="space-y-4">
          <div className="border-b pb-2">
            <h4 className="font-semibold text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Groups
            </h4>
            <p className="text-sm text-gray-600 mt-1">
              Select new admins for these groups
            </p>
          </div>

          {Object.entries(adminTransferData.groups).map(([groupId, groupData]) => (
            <div key={groupId} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h5 className="font-medium">{groupData.name}</h5>
                  <p className="text-xs text-gray-500">in {groupData.organizationName}</p>
                </div>
                {groupData.transferred && (
                  <span className="text-green-600 text-sm flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Transferred
                  </span>
                )}
              </div>

              {groupData.members.length === 0 ? (
                <div className="text-sm text-gray-500 italic p-4 border border-dashed rounded">
                  No other members in this group
                </div>
              ) : (
                <>
                  <div className="space-y-2 mb-4">
                    {groupData.members.map((member) => (
                      <label
                        key={member.userId}
                        className="flex items-center gap-3 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={groupData.selectedAdmins.includes(member.userId)}
                          onChange={(e) => handleGroupAdminSelection(groupId, member.userId, e.target.checked)}
                          disabled={groupData.transferred}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{member.name}</div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                          <div className="text-xs text-gray-400">Current: {member.currentRole}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <Button
                    onClick={() => handleConfirmGroupAdminTransfer(groupId)}
                    disabled={groupData.transferred || groupData.selectedAdmins.length === 0 || loading}
                    className="w-full"
                  >
                    {groupData.transferred ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Admin Transferred
                      </>
                    ) : (
                      <>Assign {groupData.selectedAdmins.length || 0} Selected as Admin</>
                    )}
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={onClose} variant="outline" className="flex-1">
          Back
        </Button>
        <Button
          onClick={() => setStep('dependencies')}
          className="flex-1"
          disabled={!canProceedAfterTransfer() || loading}
        >
          Continue to Review
        </Button>
      </div>
    </div>
  );

  const renderDependenciesStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Database className="w-16 h-16 text-orange-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Account Dependencies</h3>
        <p className="text-gray-600">
          Here's what will be affected by your account deletion:
        </p>
      </div>


      {dependencies && (
        <div className="space-y-4">
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Building className="w-4 h-4" />
              <h4 className="font-medium">Organizations</h4>
            </div>
            <p className="text-sm text-gray-600">
              You are a member of {dependencies.organizations.count} organization(s)
            </p>
            {dependencies.organizations.names.length > 0 && (
              <ul className="text-sm text-gray-700 mt-1">
                {dependencies.organizations.names.map((name, index) => (
                  <li key={index}>• {name}</li>
                ))}
              </ul>
            )}
          </div>

          {dependencies.organizations_user_is_only_member > 0 && (
            <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <h4 className="font-medium text-red-900">Organizations You Own</h4>
              </div>
              <p className="text-sm text-red-700">
                You are the only member of {dependencies.organizations_user_is_only_member} organization(s).
                {deletionType === 'full'
                  ? ' These will be permanently deleted.'
                  : ' These will be kept but you will be removed.'
                }
              </p>
            </div>
          )}

          {dependencies.groups && (
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4" />
                <h4 className="font-medium">Groups</h4>
              </div>
              <p className="text-sm text-gray-600">
                You are a member of {dependencies.groups.count} group(s)
              </p>
              {dependencies.groups.names.length > 0 && (
                <ul className="text-sm text-gray-700 mt-1">
                  {dependencies.groups.names.map((name, index) => (
                    <li key={index}>• {name}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {dependencies.groups_user_is_only_member > 0 && (
            <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <h4 className="font-medium text-red-900">Groups You Own</h4>
              </div>
              <p className="text-sm text-red-700">
                You are the only member of {dependencies.groups_user_is_only_member} group(s).
                {deletionType === 'full'
                  ? ' These will be permanently deleted.'
                  : ' These will be kept but you will be removed.'
                }
              </p>
            </div>
          )}

          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4" />
              <h4 className="font-medium">Your Data</h4>
            </div>
            <p className="text-sm text-gray-600">
              You have {dependencies.user_data_records} data records that will be deleted
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={onClose} variant="outline" className="flex-1">
          Back
        </Button>
        <Button
          onClick={() => setStep('confirmation')}
          className="flex-1"
        >
          Continue
        </Button>
      </div>
    </div>
  );

  const renderConfirmationStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Trash2 className="w-16 h-16 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Final Confirmation</h3>
        <p className="text-gray-600">
          Please type your email address to confirm account deletion
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="confirmation">Type your email: {user?.email}</Label>
          <Input
            id="confirmation"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder={user?.email}
            className="text-center"
          />
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-900">This action is irreversible</h4>
              <p className="text-sm text-red-700 mt-1">
                Once you delete your account, all your data will be permanently removed and cannot be recovered.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={() => setStep('dependencies')} variant="outline" className="flex-1">
          Back
        </Button>
        <Button
          onClick={handleDeleteAccount}
          disabled={confirmationText !== user?.email || loading}
          variant="destructive"
          className="flex-1"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Deleting...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="space-y-6 text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto"></div>
      <h3 className="text-lg font-semibold">Deleting Your Account</h3>
      <p className="text-gray-600">
        Please wait while we securely delete your account and all associated data...
      </p>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Delete Account
          </CardTitle>
          <CardDescription>
            Permanently delete your account and all associated data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === null && (
            <div className="space-y-6 text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <h3 className="text-lg font-semibold">Preparing deletion process...</h3>
            </div>
          )}
          {step === 'admin_transfer' && renderAdminTransferStep()}
          {step === 'dependencies' && renderDependenciesStep()}
          {step === 'confirmation' && renderConfirmationStep()}
          {step === 'processing' && renderProcessingStep()}
        </CardContent>
      </Card>
    </div>
  );
};
